<script lang="ts" setup>
import type { Pagination } from '~/types/Pagination';
import { cloneValue } from '~/utils/cloneValue';

const props = defineProps<{ pagination: Pagination }>();
const route = useRoute();

const isShowPagination = computed(() => props.pagination.total > props.pagination.per_page);

const pagesCount = computed(() => Math.ceil(props.pagination.total / props.pagination.per_page));
const isLastPage = computed(() => props.pagination.page === pagesCount.value);
const viewableRange = computed(() => (props.pagination.page > 1 && props.pagination.page < pagesCount.value ? 1 : 3));

const rangeStart = computed(() => {
  const start = props.pagination.page - viewableRange.value;
  return start > 0 ? start : 1;
});

const rangeEnd = computed(() => {
  const end = props.pagination.page + viewableRange.value;
  return end < pagesCount.value ? end : pagesCount.value;
});

const pages = computed(() => {
  const result = [];

  for (let i = rangeStart.value; i <= rangeEnd.value; i++) {
    result.push(i);
  }

  return result;
});

async function setActivePage(page: number) {
  if (page === props.pagination.page) {
    return;
  } else if (page === 1) {
    const query = cloneValue(route.query);
    delete query.page;

    await navigateTo({ path: route.path, query });
  } else {
    await navigateTo({ path: route.path, query: { ...route.query, page } });
  }
}

function goToPrevPage() {
  if (props.pagination.page !== 1) {
    setActivePage(props.pagination.page - 1);
  }
}

function goToNextPage() {
  if (!isLastPage.value) {
    setActivePage(props.pagination.page + 1);
  }
}

function showPrevRange() {
  const newActivePage =
    props.pagination.page - viewableRange.value - 1 > 0 ? props.pagination.page - viewableRange.value - 1 : 1;

  setActivePage(newActivePage);
}

function showNextRange() {
  const newActivePage =
    props.pagination.page + viewableRange.value + 1 > pagesCount.value
      ? pagesCount.value
      : props.pagination.page + viewableRange.value + 1;

  setActivePage(newActivePage);
}
</script>

<template>
  <section
    v-if="isShowPagination"
    class="pagination-block"
  >
    <button
      :disabled="pagination.page === 1"
      class="pagination-block__page-btn"
      @click="goToPrevPage"
    >
      <SvgoChevronLeft
        :font-controlled="false"
        class="size-5"
        filled
      />
    </button>

    <button
      v-if="rangeStart > 1"
      :class="{ 'pagination-block__page-btn--active': pagination.page === 1 }"
      class="pagination-block__page-btn"
      @click="setActivePage(1)"
    >
      1
    </button>

    <button
      v-if="rangeStart > 2"
      class="pagination-block__page-btn"
      @click="showPrevRange"
    >
      &hellip;
    </button>

    <button
      v-for="page in pages"
      :key="page"
      :class="{ 'pagination-block__page-btn--active': pagination.page === page }"
      class="pagination-block__page-btn"
      @click="setActivePage(page)"
    >
      {{ page }}
    </button>

    <button
      v-if="rangeEnd < pagesCount - 1"
      class="pagination-block__page-btn"
      @click="showNextRange"
    >
      &hellip;
    </button>

    <button
      v-if="rangeEnd < pagesCount"
      :class="{ 'pagination-block__page-btn--active': pagination.page === pagesCount }"
      class="pagination-block__page-btn"
      @click="setActivePage(pagesCount)"
    >
      {{ pagesCount }}
    </button>

    <button
      :disabled="isLastPage"
      class="pagination-block__page-btn"
      @click="goToNextPage"
    >
      <SvgoChevronRight
        :font-controlled="false"
        class="size-5"
        filled
      />
    </button>
  </section>
</template>

<style lang="scss" scoped>
.pagination-block {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;

  &__page-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid $black-20;
    border-radius: 8px;
    background-color: $white;
    color: $black;
    font-size: $text-sm;
    transition: all 0.3s;

    @include screen-sm {
      width: 40px;
      height: 40px;
    }

    &:hover {
      border-color: $black-60;
    }

    &--active {
      border-color: $black;
      background-color: $black;
      color: $white;

      &:hover {
        background-color: $black-80;
      }
    }

    &:disabled {
      border-color: $black-10;
      color: $black-20;
    }
  }
}
</style>
